import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage,
} from "../../common/APICommon";
import { LoadingSpinner } from "../../common/Loading";
import { formatDateLong } from "../../utils/textUtils";

import { useVessel201 } from "./shared/useVessel201";
import { Vessel201Modal } from "./shared/Vessel201Modal";
import { DateInput } from "../../common/Input";
import { VesselPickerField } from "../../common/selectComponents/VesselPicker";

function Vessel201({ user, isHideSideMenu }) {
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  const {
    loading,
    dropdownsLoading,
    requestLoading,
    data,
    options,
    filterEffectiveDateFrom,
    setFilterEffectiveDateFrom,
    filterEffectiveDateTo,
    setFilterEffectiveDateTo,
    setIsGenerate,

    // --new
    fetchData,
    fetchDataByVesselIdEffectiveDate,
    fetchVesselEffectiveDates,
    fetchDropdowns,
    selectedDataEffectiveDates,
    setSelectedDataEffectiveDates,
    selectedData,
    setSelectedData,

    handleAdd,
    handleUpdate,
    handleFilter
  } = useVessel201({ user, showGeneralMessage });

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [currentPage, setCurrentPage] = useState(1);

  const openModal = async (item = null, formMode = "add") => {
    if (item?.vesselId) {
      const resultDetails = await fetchDataByVesselIdEffectiveDate(item.vesselId, item?.effectiveDate);
      const resultEffectiveDates = await fetchVesselEffectiveDates(item.vesselId);

      if (resultDetails && resultEffectiveDates.length > 0) {
        setSelectedData(resultDetails);
        setSelectedDataEffectiveDates(resultEffectiveDates);
      }
    } else {
      setSelectedData(null);
      setSelectedDataEffectiveDates(null);
      setMode("add");
    }

    setMode(formMode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedData(null);
    setSelectedDataEffectiveDates(null);
  }

  // ── Table config ──────────────────────────────────────────────────────────
  const headers = [
    "Vessel Code",
    "Registered Name",
    "Vessel Name",
    "Status",
    "Effectivity Date",
    "Actions",
  ];
  const accessors = [
    "vesselCode",
    "vesselRegisteredName",
    "vesselName",
    "vesselStatus",
    "effectiveDate",
    "actions",
  ];

  const actions = [
    {
      name: "Edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: (item) => openModal(item, "edit"),
      disabled: (item) => { 
        const hasRecord = item?.id !== null && item?.id !== undefined;
        return !hasRecord;
      },
      visible: true,
    },
    {
      name: "View",
      icon: <VisibilityIcon fontSize="small" />,
      className: "bg-blue-500 hover:bg-blue-600 flex items-center",
      function: (item) => openModal(item, "view"),
      disabled: (item) => { 
        const hasRecord = item?.id !== null && item?.id !== undefined;
        return !hasRecord;
      },
      visible: true,
    },
  ];

  const renderCustomCell = (row, accessor) => {
    if (accessor === "effectiveDate") {
      return row[accessor] ? formatDateLong(row[accessor]) : "-";
    }
    return null;
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
      <div className="LeftSpace" />
      <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
        <div className="MainteInfo shadow-xl">
          <div className="mainteHeader">
            <div className="sub-title row">
              <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>
                {"Vessel Module > "}
              </span>
              <span style={{ borderBottom: "1px solid black" }}>{"Vessel 201"}</span>
            </div>
            <h3>Vessel 201</h3>
          </div>

          <div className="p-4 space-y-4">

            <div className='filters-payroll'>
              <span className="text-gray-700 font-medium">Filter</span>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 my-3">
                <DateInput
                  label="Filter date from:"
                  value={filterEffectiveDateFrom}
                  onChange={(e) => setFilterEffectiveDateFrom(e.target.value)}
                  placeholder="Select effective date"
                />
                <DateInput
                  label="Filter date to:"
                  value={filterEffectiveDateTo}
                  onChange={(e) => setFilterEffectiveDateTo(e.target.value)}
                  placeholder="Select effective date"
                />
              </div>

              <div className="flex row gap-2">
                <Button
                  name={"Generate"}
                  className={"bg-blue-500 hover:bg-blue-600 border-none cursor-pointer"}
                  onClick={() => {

                    if (!filterEffectiveDateFrom || !filterEffectiveDateTo) {
                      showGeneralMessage("Please select both 'From' and 'To' dates.", false);
                      return;
                    }

                    if (filterEffectiveDateFrom > filterEffectiveDateTo) {
                      showGeneralMessage("'Date From' cannot be later than 'Date To'.", false);
                      return;
                    }

                    fetchData(filterEffectiveDateFrom, filterEffectiveDateTo);

                  }}
                />

                <Button
                  name={"Clear"}
                  className={"bg-gray-400 hover:bg-gray-500 border-none cursor-pointer"}
                  onClick={() => {
                    setFilterEffectiveDateFrom("");
                    setFilterEffectiveDateTo("");
                    fetchData();
                  }}
                />
              </div>
            </div>

            <SearchBar handleFilter={handleFilter} />

            <Button
              icon={<AddIcon />}
              name="New Transaction"
              className="bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"
              onClick={() => openModal(null, "add")}
            />
            <CreateTable
              tableName="Vessel201Table"
              headers={headers}
              accessors={accessors}
              data={data}
              actions={actions}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={10}
              totalItems={data?.length}
              customCellRenderer={renderCustomCell}
            />
          </div>
        </div>
      </div>

      {showModal && (
        <Vessel201Modal
          isOpen={showModal}
          onClose={() => closeModal()}
          mode={mode}
          selectedData={selectedData}
          setSelectedData={setSelectedData}
          selectedDataEffectiveDates={selectedDataEffectiveDates}
          options={options}
          dropdownsLoading={dropdownsLoading}
          onAdd={handleAdd}
          onUpdate={handleUpdate}
          fetchDataByVesselIdEffectiveDate={fetchDataByVesselIdEffectiveDate}
          fetchDropdowns={fetchDropdowns}
        />
      )}

      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
      <LoadingSpinner loading={loading || dropdownsLoading || requestLoading} />
    </div>
  );
}

export default Vessel201;